import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

export interface Role {
  id: number;
  name: string;
}

export interface Permission {
  id: number;
  permission_name: string;
  table_name: string;
  target_id: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = '/api';
  private currentUserRoles = new BehaviorSubject<Role[]>([]);
  public roles$ = this.currentUserRoles.asObservable();

  constructor(private http: HttpClient, private router: Router) {
    this.loadUserRoles();
  }

  private loadUserRoles(): void {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user.roles && Array.isArray(user.roles)) {
        this.currentUserRoles.next(user.roles);
      }
    }
  }

  login(username: string, password: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(`/api/auth/login`, {
      username,
      password
    }, { headers }).pipe(
      tap((response: any) => {
        if (response.success) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          if (response.data.user.roles) {
            this.currentUserRoles.next(response.data.user.roles);
          }
          if (response.data.permissions) {
            localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
          }
          if (response.data.user.subsidiary_id) {
            localStorage.setItem('currentSubsidiaryId', response.data.user.subsidiary_id.toString());
          }
          this.http.get<any>(`${this.apiUrl}/user/permissions`)
            .subscribe(async permissionsResponse => {
              if (permissionsResponse && permissionsResponse.success) {
                localStorage.setItem('user_permissions',
                  JSON.stringify(permissionsResponse.data)
                );
                if (permissionsResponse.data.tablePermissions.create_table === true) {
                  await this.router.navigate(['/dashboard']);
                  window.location.reload();
                } else {
                  await this.router.navigate(['/dashboard/data']);
                  window.location.reload();
                }
              }
            });
        }
      }),
      catchError(error => {
        return throwError(() => error.error || 'Đã xảy ra lỗi trong quá trình đăng nhập');
      })
    );
  }

  setupPermission() {
    this.http.get<any>(`${this.apiUrl}/user/permissions`)
      .subscribe(async permissionsResponse => {
        if (permissionsResponse && permissionsResponse.success) {
          localStorage.setItem('user_permissions',
            JSON.stringify(permissionsResponse.data)
          );
        }
      });
  }

  register(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/register`, data)
      .pipe(
        catchError(error => {
          return throwError(() => error.error || 'Đã xảy ra lỗi trong quá trình đăng ký');
        })
      );
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/forgot-password`, { email })
      .pipe(
        catchError(error => {
          return throwError(() => error.error || 'Không thể gửi email đặt lại mật khẩu');
        })
      );
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('currentSubsidiaryId');
    this.currentUserRoles.next([]);
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getCurrentUser(): any {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getUserRoles(): Role[] {
    const user = this.getCurrentUser();
    return user && user.roles ? user.roles : [];
  }

  isInRole(roleName: string): boolean {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.roles) return false;

    return currentUser.roles.some((role: { name: string; }) => role.name === roleName);
  }

  isOwner(): boolean {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.roles) return false;

    return currentUser.roles.some((role: { name: string; }) => role.name === 'owner');
  }
  isAdmin(): boolean {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.roles) return false;

    return currentUser.roles.some((role: { name: string; }) => role.name === 'admin');
  }

  getPermissions(): any[] {
    const permissionsStr = localStorage.getItem('permissions');
    return permissionsStr ? JSON.parse(permissionsStr) : [];
  }

  hasPermission(tableId: number, permissionName: string): boolean {
    const permissions = this.getPermissions();

    return permissions.some(p =>
      p.table_id === tableId &&
      p.permission_name === permissionName
    );
  }

  getUserSubsidiaries(): any[] {
    const permissions = this.getPermissions();
    if (!permissions || permissions.length === 0) {
      return [];
    }

    const subsidiaryMap = new Map();
    permissions.forEach(p => {
      if (p.subsidiary_id && p.subsidiary_name) {
        subsidiaryMap.set(p.subsidiary_id, {
          id: p.subsidiary_id,
          name: p.subsidiary_name
        });
      }
    });

    return Array.from(subsidiaryMap.values());
  }

  getCurrentSubsidiaryId(): number | null {
    const subsidiaryId = localStorage.getItem('currentSubsidiaryId');
    return subsidiaryId ? Number(subsidiaryId) : null;
  }

  setCurrentSubsidiaryId(subsidiaryId: number): void {
    localStorage.setItem('currentSubsidiaryId', subsidiaryId.toString());
  }

  refreshUserData(): Observable<any> {
    return this.http.get(`${this.apiUrl}/auth/me`).pipe(
      tap((response: any) => {
        if (response.success) {
          localStorage.setItem('user', JSON.stringify(response.data));

          if (response.data.roles) {
            this.currentUserRoles.next(response.data.roles);
          }

          if (response.data.permissions) {
            localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
          }
        }
      }),
      catchError(error => {
        return throwError(() => error.error || 'Không thể cập nhật thông tin người dùng');
      })
    );
  }

}